import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Building2, ArrowRight, ArrowLeft, CheckCircle2, Upload, Eye, EyeOff, ShieldCheck, FileText, CreditCard, MapPin, Calendar } from 'lucide-react';
import { useAppContext } from '../contexts/AppContext';
import { toast } from 'sonner';
import { BillvastLogo } from '../components/ui/BillvastLogo';
export const Signup: React.FC = () => {
  const navigate = useNavigate();
  const {
    login,
    dispatch
  } = useAppContext();
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  // Step 1: Account Type
  const [accountType, setAccountType] = useState<'individual' | 'organization'>('individual');
  // Step 2: Basic Info
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [cacNumber, setCacNumber] = useState('');
  const [ageError, setAgeError] = useState('');
  // Step 3: KYC
  // Individual
  const [idType, setIdType] = useState('nin');
  const [idNumber, setIdNumber] = useState('');
  const [idUploaded, setIdUploaded] = useState(false);
  const [bvn, setBvn] = useState('');
  const [bankName, setBankName] = useState('gtbank');
  const [accountNumber, setAccountNumber] = useState('');
  const [addressType, setAddressType] = useState('utility_bill');
  const [addressUploaded, setAddressUploaded] = useState(false);
  // Organization
  const [cacUploaded, setCacUploaded] = useState(false);
  const [officeAddress, setOfficeAddress] = useState('');
  // Step 4: Review
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const validateAge = (dob: string) => {
    if (!dob) return false;
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || m === 0 && today.getDate() < birthDate.getDate()) {
      age--;
    }
    if (age < 18) {
      setAgeError('You must be at least 18 years old to register.');
      return false;
    }
    setAgeError('');
    return true;
  };
  const handleNext = () => {
    if (step === 2) {
      if (!name || !email || !phone || !password || !confirmPassword || !dateOfBirth) {
        toast.error('Please fill in all required fields');
        return;
      }
      if (password !== confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
      if (password.length < 8) {
        toast.error('Password must be at least 8 characters');
        return;
      }
      if (!validateAge(dateOfBirth)) {
        return;
      }
      if (accountType === 'organization' && (!organizationName || !cacNumber)) {
        toast.error('Please provide organization details');
        return;
      }
    }
    if (step === 3) {
      if (accountType === 'individual') {
        if (!idNumber || !idUploaded || !bvn || !accountNumber || !addressUploaded) {
          toast.error('Please complete all KYC requirements');
          return;
        }
        if (bvn.length !== 11) {
          toast.error('BVN must be 11 digits');
          return;
        }
        if (accountNumber.length !== 10) {
          toast.error('Account number must be 10 digits');
          return;
        }
      } else {
        if (!cacUploaded || !bvn || !accountNumber || !officeAddress) {
          toast.error('Please complete all KYC requirements');
          return;
        }
        if (bvn.length !== 11) {
          toast.error('BVN must be 11 digits');
          return;
        }
        if (accountNumber.length !== 10) {
          toast.error('Account number must be 10 digits');
          return;
        }
      }
    }
    setDirection(1);
    setStep(prev => Math.min(prev + 1, 4));
  };
  const handleBack = () => {
    setDirection(-1);
    setStep(prev => Math.max(prev - 1, 1));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreedToTerms) {
      toast.error('You must agree to the Terms of Service');
      return;
    }
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      // Update user in context
      dispatch({
        type: 'UPDATE_USER',
        payload: {
          name,
          email,
          phone,
          dateOfBirth,
          accountType,
          ...(accountType === 'organization' ? {
            organizationName,
            cacNumber,
            officeAddress
          } : {})
        }
      });
      login();
      toast.success('Account created successfully! Welcome to Billvast');
      navigate('/dashboard');
    }, 2000);
  };
  const simulateUpload = (setter: React.Dispatch<React.SetStateAction<boolean>>) => {
    toast.success('Document uploaded successfully');
    setter(true);
  };
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 50 : -50,
      opacity: 0
    })
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8" data-id="element-1878">
      <div className="sm:mx-auto sm:w-full sm:max-w-xl" data-id="element-1879">
        <div className="flex justify-center mb-6" data-id="element-1880">
          <BillvastLogo size="xl" data-id="element-1881" />
        </div>

        {/* Step Indicator */}
        <div className="mb-8 px-4" data-id="element-1882">
          <div className="flex items-center justify-between relative" data-id="element-1883">
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-gray-200 -z-10 rounded-full" data-id="element-1884"></div>
            <div className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-brand-primary -z-10 rounded-full transition-all duration-500" style={{
            width: `${(step - 1) / 3 * 100}%`
          }} data-id="element-1885"></div>

            {[1, 2, 3, 4].map(s => <div key={s} className="flex flex-col items-center gap-2 bg-gray-50 px-2" data-id="element-1886">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors duration-300 ${step > s ? 'bg-brand-primary text-white' : step === s ? 'bg-white border-2 border-brand-primary text-brand-primary ring-4 ring-brand-primary/20' : 'bg-gray-200 text-gray-500'}`} data-id="element-1887">
                  {step > s ? <CheckCircle2 size={16} data-id="element-1888" /> : s}
                </div>
              </div>)}
          </div>
          <div className="flex justify-between mt-2 px-1" data-id="element-1889">
            <span className="text-xs font-medium text-brand-text-dark" data-id="element-1890">
              Type
            </span>
            <span className="text-xs font-medium text-brand-text-dark" data-id="element-1891">
              Info
            </span>
            <span className="text-xs font-medium text-brand-text-dark" data-id="element-1892">
              KYC
            </span>
            <span className="text-xs font-medium text-brand-text-dark" data-id="element-1893">
              Review
            </span>
          </div>
        </div>

        <div className="bg-white py-8 px-4 shadow-xl shadow-gray-200/50 sm:rounded-2xl sm:px-10 border border-gray-100 overflow-hidden relative min-h-[400px]" data-id="element-1894">
          <AnimatePresence mode="wait" custom={direction} data-id="element-1895">
            <motion.div key={step} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
            duration: 0.3,
            ease: 'easeInOut'
          }} className="w-full" data-id="element-1896">
              {/* STEP 1: Account Type */}
              {step === 1 && <div className="space-y-6" data-id="element-1897">
                  <div className="text-center mb-8" data-id="element-1898">
                    <h2 className="text-2xl font-bold text-brand-text-dark" data-id="element-1899">
                      Choose Account Type
                    </h2>
                    <p className="text-sm text-brand-text-light mt-2" data-id="element-1900">
                      Select how you want to use Billvast
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1901">
                    <button onClick={() => setAccountType('individual')} className={`p-6 rounded-2xl border-2 text-left transition-all ${accountType === 'individual' ? 'border-brand-primary bg-brand-primary/5 ring-4 ring-brand-primary/10' : 'border-gray-200 hover:border-brand-primary/50 hover:bg-gray-50'}`} data-id="element-1902">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${accountType === 'individual' ? 'bg-brand-primary text-white' : 'bg-gray-100 text-gray-500'}`} data-id="element-1903">
                        <User size={24} data-id="element-1904" />
                      </div>
                      <h3 className="text-lg font-bold text-brand-text-dark mb-2" data-id="element-1905">
                        Personal Account
                      </h3>
                      <p className="text-sm text-brand-text-light leading-relaxed" data-id="element-1906">
                        For individuals who want to donate, fundraise, or manage
                        personal finances
                      </p>
                    </button>

                    <button onClick={() => setAccountType('organization')} className={`p-6 rounded-2xl border-2 text-left transition-all ${accountType === 'organization' ? 'border-brand-primary bg-brand-primary/5 ring-4 ring-brand-primary/10' : 'border-gray-200 hover:border-brand-primary/50 hover:bg-gray-50'}`} data-id="element-1907">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${accountType === 'organization' ? 'bg-brand-primary text-white' : 'bg-gray-100 text-gray-500'}`} data-id="element-1908">
                        <Building2 size={24} data-id="element-1909" />
                      </div>
                      <h3 className="text-lg font-bold text-brand-text-dark mb-2" data-id="element-1910">
                        Organization Account
                      </h3>
                      <p className="text-sm text-brand-text-light leading-relaxed" data-id="element-1911">
                        For NGOs, businesses, and registered organizations
                      </p>
                    </button>
                  </div>
                </div>}

              {/* STEP 2: Basic Info */}
              {step === 2 && <div className="space-y-5" data-id="element-1912">
                  <div className="text-center mb-6" data-id="element-1913">
                    <h2 className="text-2xl font-bold text-brand-text-dark" data-id="element-1914">
                      Basic Information
                    </h2>
                    <p className="text-sm text-brand-text-light mt-1" data-id="element-1915">
                      Tell us about yourself
                    </p>
                  </div>

                  {accountType === 'organization' && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1916">
                      <div data-id="element-1917">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1918">
                          Organization Name *
                        </label>
                        <input type="text" value={organizationName} onChange={e => setOrganizationName(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none" placeholder="e.g. Hope Foundation" data-id="element-1919" />
                      </div>
                      <div data-id="element-1920">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1921">
                          CAC Number *
                        </label>
                        <input type="text" value={cacNumber} onChange={e => setCacNumber(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none" placeholder="e.g. RC123456" data-id="element-1922" />
                      </div>
                    </div>}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1923">
                    <div data-id="element-1924">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1925">
                        Full Name *
                      </label>
                      <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none" placeholder="John Doe" data-id="element-1926" />
                    </div>
                    <div data-id="element-1927">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1928">
                        Date of Birth *
                      </label>
                      <input type="date" value={dateOfBirth} onChange={e => {
                    setDateOfBirth(e.target.value);
                    validateAge(e.target.value);
                  }} className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:outline-none ${ageError ? 'border-red-300 focus:ring-red-200 focus:border-red-500' : 'border-gray-300 focus:ring-brand-primary focus:border-brand-primary'}`} data-id="element-1929" />
                      {ageError && <p className="text-xs text-red-500 mt-1" data-id="element-1930">{ageError}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1931">
                    <div data-id="element-1932">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1933">
                        Email Address *
                      </label>
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none" placeholder="john@example.com" data-id="element-1934" />
                    </div>
                    <div data-id="element-1935">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1936">
                        Phone Number *
                      </label>
                      <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none" placeholder="+234 800 000 0000" data-id="element-1937" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1938">
                    <div data-id="element-1939">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1940">
                        Password *
                      </label>
                      <div className="relative" data-id="element-1941">
                        <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none pr-10" placeholder="Min. 8 characters" data-id="element-1942" />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-1943">
                          {showPassword ? <EyeOff size={18} data-id="element-1944" /> : <Eye size={18} data-id="element-1945" />}
                        </button>
                      </div>
                    </div>
                    <div data-id="element-1946">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1947">
                        Confirm Password *
                      </label>
                      <input type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none" placeholder="Confirm password" data-id="element-1948" />
                    </div>
                  </div>
                </div>}

              {/* STEP 3: KYC */}
              {step === 3 && <div className="space-y-5" data-id="element-1949">
                  <div className="text-center mb-6" data-id="element-1950">
                    <h2 className="text-2xl font-bold text-brand-text-dark" data-id="element-1951">
                      Identity Verification
                    </h2>
                    <p className="text-sm text-brand-text-light mt-1" data-id="element-1952">
                      Please provide your KYC documents
                    </p>
                  </div>

                  {accountType === 'individual' ? <>
                      {/* Individual KYC */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1953">
                        <div data-id="element-1954">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1955">
                            Government ID Type
                          </label>
                          <select value={idType} onChange={e => setIdType(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none bg-white" data-id="element-1956">
                            <option value="nin" data-id="element-1957">NIN Slip</option>
                            <option value="voters_card" data-id="element-1958">Voter's Card</option>
                            <option value="drivers_license" data-id="element-1959">
                              Driver's License
                            </option>
                            <option value="passport" data-id="element-1960">
                              International Passport
                            </option>
                          </select>
                        </div>
                        <div data-id="element-1961">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1962">
                            ID Number
                          </label>
                          <input type="text" value={idNumber} onChange={e => setIdNumber(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none uppercase" placeholder="Enter ID Number" data-id="element-1963" />
                        </div>
                      </div>

                      <div data-id="element-1964">
                        <button type="button" onClick={() => simulateUpload(setIdUploaded)} className={`w-full py-4 border-2 border-dashed rounded-xl flex flex-col items-center justify-center gap-2 transition-colors ${idUploaded ? 'border-green-300 bg-green-50 text-green-700' : 'border-gray-300 hover:border-brand-primary hover:bg-brand-primary/5 text-gray-500'}`} data-id="element-1965">
                          {idUploaded ? <CheckCircle2 size={24} data-id="element-1966" /> : <Upload size={24} data-id="element-1967" />}
                          <span className="font-medium text-sm" data-id="element-1968">
                            {idUploaded ? 'ID Document Uploaded' : 'Upload ID Document'}
                          </span>
                        </button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1969">
                        <div data-id="element-1970">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1971">
                            Bank Verification Number (BVN)
                          </label>
                          <input type="text" maxLength={11} value={bvn} onChange={e => setBvn(e.target.value.replace(/\D/g, ''))} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none font-mono" placeholder="11-digit BVN" data-id="element-1972" />
                        </div>
                        <div data-id="element-1973">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1974">
                            Bank Name
                          </label>
                          <select value={bankName} onChange={e => setBankName(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none bg-white" data-id="element-1975">
                            <option value="gtbank" data-id="element-1976">GTBank</option>
                            <option value="access" data-id="element-1977">Access Bank</option>
                            <option value="firstbank" data-id="element-1978">First Bank</option>
                            <option value="uba" data-id="element-1979">UBA</option>
                            <option value="zenith" data-id="element-1980">Zenith Bank</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1981">
                        <div data-id="element-1982">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1983">
                            Account Number
                          </label>
                          <input type="text" maxLength={10} value={accountNumber} onChange={e => setAccountNumber(e.target.value.replace(/\D/g, ''))} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none font-mono" placeholder="10-digit Account Number" data-id="element-1984" />
                        </div>
                        <div data-id="element-1985">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1986">
                            Proof of Address Type
                          </label>
                          <select value={addressType} onChange={e => setAddressType(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none bg-white" data-id="element-1987">
                            <option value="utility_bill" data-id="element-1988">Utility Bill</option>
                            <option value="bank_statement" data-id="element-1989">
                              Bank Statement
                            </option>
                            <option value="tenancy_agreement" data-id="element-1990">
                              Tenancy Agreement
                            </option>
                          </select>
                        </div>
                      </div>

                      <div data-id="element-1991">
                        <button type="button" onClick={() => simulateUpload(setAddressUploaded)} className={`w-full py-4 border-2 border-dashed rounded-xl flex flex-col items-center justify-center gap-2 transition-colors ${addressUploaded ? 'border-green-300 bg-green-50 text-green-700' : 'border-gray-300 hover:border-brand-primary hover:bg-brand-primary/5 text-gray-500'}`} data-id="element-1992">
                          {addressUploaded ? <CheckCircle2 size={24} data-id="element-1993" /> : <Upload size={24} data-id="element-1994" />}
                          <span className="font-medium text-sm" data-id="element-1995">
                            {addressUploaded ? 'Address Document Uploaded' : 'Upload Proof of Address'}
                          </span>
                        </button>
                      </div>
                    </> : <>
                      {/* Organization KYC */}
                      <div data-id="element-1996">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1997">
                          CAC Certificate
                        </label>
                        <button type="button" onClick={() => simulateUpload(setCacUploaded)} className={`w-full py-6 border-2 border-dashed rounded-xl flex flex-col items-center justify-center gap-2 transition-colors ${cacUploaded ? 'border-green-300 bg-green-50 text-green-700' : 'border-gray-300 hover:border-brand-primary hover:bg-brand-primary/5 text-gray-500'}`} data-id="element-1998">
                          {cacUploaded ? <CheckCircle2 size={24} data-id="element-1999" /> : <FileText size={24} data-id="element-2000" />}
                          <span className="font-medium text-sm" data-id="element-2001">
                            {cacUploaded ? 'CAC Certificate Uploaded' : 'Upload CAC Certificate'}
                          </span>
                        </button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2002">
                        <div data-id="element-2003">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2004">
                            Signatory BVN
                          </label>
                          <input type="text" maxLength={11} value={bvn} onChange={e => setBvn(e.target.value.replace(/\D/g, ''))} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none font-mono" placeholder="11-digit BVN" data-id="element-2005" />
                        </div>
                        <div data-id="element-2006">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2007">
                            Corporate Bank Name
                          </label>
                          <select value={bankName} onChange={e => setBankName(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none bg-white" data-id="element-2008">
                            <option value="gtbank" data-id="element-2009">GTBank</option>
                            <option value="access" data-id="element-2010">Access Bank</option>
                            <option value="firstbank" data-id="element-2011">First Bank</option>
                            <option value="uba" data-id="element-2012">UBA</option>
                            <option value="zenith" data-id="element-2013">Zenith Bank</option>
                          </select>
                        </div>
                      </div>

                      <div data-id="element-2014">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2015">
                          Corporate Account Number
                        </label>
                        <input type="text" maxLength={10} value={accountNumber} onChange={e => setAccountNumber(e.target.value.replace(/\D/g, ''))} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none font-mono" placeholder="10-digit Account Number" data-id="element-2016" />
                      </div>

                      <div data-id="element-2017">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2018">
                          Office Address
                        </label>
                        <textarea value={officeAddress} onChange={e => setOfficeAddress(e.target.value)} rows={3} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none resize-none" placeholder="Enter full office address" data-id="element-2019" />
                      </div>
                    </>}
                </div>}

              {/* STEP 4: Review */}
              {step === 4 && <div className="space-y-6" data-id="element-2020">
                  <div className="text-center mb-6" data-id="element-2021">
                    <h2 className="text-2xl font-bold text-brand-text-dark" data-id="element-2022">
                      Review & Submit
                    </h2>
                    <p className="text-sm text-brand-text-light mt-1" data-id="element-2023">
                      Please verify your information
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-2xl p-5 border border-gray-200 space-y-4" data-id="element-2024">
                    <div className="flex items-center justify-between pb-4 border-b border-gray-200" data-id="element-2025">
                      <div className="flex items-center gap-3" data-id="element-2026">
                        <div className="w-10 h-10 rounded-full bg-brand-primary/10 text-brand-primary flex items-center justify-center" data-id="element-2027">
                          {accountType === 'individual' ? <User size={20} data-id="element-2028" /> : <Building2 size={20} data-id="element-2029" />}
                        </div>
                        <div data-id="element-2030">
                          <p className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-2031">
                            Account Type
                          </p>
                          <p className="font-bold text-brand-text-dark capitalize" data-id="element-2032">
                            {accountType}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-y-4 gap-x-2" data-id="element-2033">
                      {accountType === 'organization' && <>
                          <div data-id="element-2034">
                            <p className="text-xs text-gray-500 mb-1" data-id="element-2035">
                              Organization Name
                            </p>
                            <p className="text-sm font-medium text-brand-text-dark" data-id="element-2036">
                              {organizationName}
                            </p>
                          </div>
                          <div data-id="element-2037">
                            <p className="text-xs text-gray-500 mb-1" data-id="element-2038">
                              CAC Number
                            </p>
                            <p className="text-sm font-medium text-brand-text-dark" data-id="element-2039">
                              {cacNumber}
                            </p>
                          </div>
                        </>}
                      <div data-id="element-2040">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-2041">Full Name</p>
                        <p className="text-sm font-medium text-brand-text-dark" data-id="element-2042">
                          {name}
                        </p>
                      </div>
                      <div data-id="element-2043">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-2044">Email</p>
                        <p className="text-sm font-medium text-brand-text-dark truncate" data-id="element-2045">
                          {email}
                        </p>
                      </div>
                      <div data-id="element-2046">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-2047">Phone</p>
                        <p className="text-sm font-medium text-brand-text-dark" data-id="element-2048">
                          {phone}
                        </p>
                      </div>
                      <div data-id="element-2049">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-2050">
                          Date of Birth
                        </p>
                        <p className="text-sm font-medium text-brand-text-dark" data-id="element-2051">
                          {dateOfBirth}
                        </p>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-200" data-id="element-2052">
                      <p className="text-sm font-bold text-brand-text-dark mb-3" data-id="element-2053">
                        KYC Status
                      </p>
                      <div className="space-y-2" data-id="element-2054">
                        {accountType === 'individual' ? <>
                            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg" data-id="element-2055">
                              <CheckCircle2 size={16} data-id="element-2056" /> Government ID (
                              {idType.toUpperCase()})
                            </div>
                            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg" data-id="element-2057">
                              <CheckCircle2 size={16} data-id="element-2058" /> BVN & Bank Details
                            </div>
                            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg" data-id="element-2059">
                              <CheckCircle2 size={16} data-id="element-2060" /> Proof of Address
                            </div>
                          </> : <>
                            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg" data-id="element-2061">
                              <CheckCircle2 size={16} data-id="element-2062" /> CAC Certificate
                            </div>
                            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg" data-id="element-2063">
                              <CheckCircle2 size={16} data-id="element-2064" /> Signatory BVN & Bank
                              Details
                            </div>
                            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg" data-id="element-2065">
                              <CheckCircle2 size={16} data-id="element-2066" /> Office Address
                            </div>
                          </>}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3" data-id="element-2067">
                    <input type="checkbox" id="terms" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} className="mt-1 w-4 h-4 text-brand-primary focus:ring-brand-primary border-gray-300 rounded" data-id="element-2068" />
                    <label htmlFor="terms" className="text-sm text-gray-600 leading-relaxed" data-id="element-2069">
                      I agree to Billvast's{' '}
                      <a href="#" className="text-brand-primary hover:underline" data-id="element-2070">
                        Terms of Service
                      </a>{' '}
                      and{' '}
                      <a href="#" className="text-brand-primary hover:underline" data-id="element-2071">
                        Privacy Policy
                      </a>
                      . I confirm that all information provided is accurate and
                      I am at least 18 years old.
                    </label>
                  </div>
                </div>}
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="mt-8 pt-6 border-t border-gray-100 flex items-center justify-between" data-id="element-2072">
            {step > 1 ? <button type="button" onClick={handleBack} disabled={isLoading} className="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium transition-colors disabled:opacity-50" data-id="element-2073">
                <ArrowLeft size={18} data-id="element-2074" /> Back
              </button> : <div data-id="element-2075"></div>}

            {step < 4 ? <button type="button" onClick={handleNext} className="flex items-center gap-2 px-6 py-2.5 bg-brand-primary hover:bg-brand-primary-dark text-white rounded-xl font-medium transition-colors shadow-sm" data-id="element-2076">
                Continue <ArrowRight size={18} data-id="element-2077" />
              </button> : <button type="button" onClick={handleSubmit} disabled={isLoading} className="flex items-center justify-center gap-2 px-8 py-2.5 bg-brand-primary hover:bg-brand-primary-dark text-white rounded-xl font-medium transition-colors shadow-sm disabled:opacity-70" data-id="element-2078">
                {isLoading ? <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2079" />
                    Creating Account...
                  </> : 'Create Account'}
              </button>}
          </div>
        </div>

        <div className="mt-6 text-center" data-id="element-2080">
          <p className="text-sm text-gray-600" data-id="element-2081">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-brand-primary hover:text-brand-primary-dark" data-id="element-2082">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>;
};